import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const ERROR_MESSAGES: Record<string, string> = {
  access_denied: "Access was denied. You can try connecting again later.",
  missing_parameters: "Invalid OAuth callback. Please try again.",
  invalid_state: "OAuth state was invalid or expired. Please reconnect.",
  unknown_error: "OAuth callback failed due to an unknown error.",
};

export const OAuthCallback: React.FC = () => {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [message, setMessage] = useState("");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleTimoutAndCleanup = (
    action: () => void,
    delay: number
  ): (() => void) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(action, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  };

  useEffect(() => {
    const success = searchParams.get("success");
    const error = searchParams.get("error");
    const errorMessage = searchParams.get("message");
    const type = searchParams.get("type");
    const userId = searchParams.get("userId");
    const connectionId = searchParams.get("connectionId");

    const integrationName =
      type === "mcp"
        ? "MCP server"
        : type
          ? `${type.charAt(0).toUpperCase()}${type.slice(1)}`
          : "Integration";

    if (success === "true") {
      setStatus("success");
      setMessage(`${integrationName} connected successfully!`);

      // Notify parent window
      if (window.opener && type) {
        const message = {
          type: `${type}_OAUTH_SUCCESS`,
          integration: type,
          userId: userId,
          connectionId,
        };
        window.opener.postMessage(message, window.location.origin);
      }

      if (orgId && type && userId) {
        handleTimoutAndCleanup(() => {
          navigate(
            `/${orgId}/user/${userId}/settings?integration=${type}&connected=true`
          );
        }, 1500);
      }
      handleTimoutAndCleanup(() => {
        window.close();
      }, 2000);
    } else if (error) {
      setStatus("error");

      const errorMsg =
        ERROR_MESSAGES[error] ||
        errorMessage ||
        "OAuth failed. Please try again.";

      setMessage(errorMsg);

      if (window.opener && type) {
        window.opener.postMessage(
          {
            type: `${type}_OAUTH_ERROR`,
            integration: type,
            message: errorMsg,
          },
          window.location.origin
        );
      }

      // Auto-close after 3 seconds
      handleTimoutAndCleanup(() => {
        window.close();
      }, 3000);
    } else {
      setStatus("error");
      setMessage("Invalid OAuth callback. Please try again.");
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [searchParams, navigate, orgId]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: 3,
      }}
    >
      {status === "loading" && (
        <>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">Processing OAuth callback...</Typography>
        </>
      )}

      {status === "success" && (
        <>
          <Alert severity="success" sx={{ mb: 2, maxWidth: 400 }}>
            {message}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            This window will close automatically.
          </Typography>
        </>
      )}

      {status === "error" && (
        <>
          <Alert severity="error" sx={{ mb: 2, maxWidth: 400 }}>
            {message}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            This window will close automatically.
          </Typography>
        </>
      )}
    </Box>
  );
};
